'use client';

import { useEffect, useState } from 'react';

import type { AuthSession } from '@/common/types/auth-session.type';

import { manualLogout } from '@/common/utils/auth-actions.client';
import { fetchAuthSession } from '@/common/utils/auth-session.client';

export function useAuthSession(): {
  data: AuthSession | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  logout: (redirectTo?: string) => void;
  refresh: () => Promise<void>;
} {
  const [data, setData] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const isBrowser = typeof window !== 'undefined';

  useEffect(() => {
    let active = true;
    const AUTH_CHANGED_EVENT = 'auth:changed';
    (async () => {
      setLoading(true);
      const session = await fetchAuthSession();
      if (active) {
        setData(session);
        setLoading(false);
      }
    })();
    // Listen for global auth mutation events (login / logout) to refresh without hard reload
    const handler = () => {
      void (async () => {
        const session = await fetchAuthSession(true);
        if (!active) return;
        setData(session);
        setLoading(false);
      })();
    };
    if (isBrowser) {
      window.addEventListener(AUTH_CHANGED_EVENT, handler);
    }
    return () => {
      active = false;
      if (isBrowser) {
        window.removeEventListener(AUTH_CHANGED_EVENT, handler);
      }
    };
  }, []);

  return {
    data,
    status: loading ? 'loading' : data ? 'authenticated' : 'unauthenticated',
    logout: (redirectTo?: string) => {
      manualLogout(redirectTo);
      if (isBrowser) {
        // Broadcast change for components that don't force reload
        window.dispatchEvent(new Event('auth:changed'));
      }
    },
    refresh: async () => {
      setLoading(true);
      const session = await fetchAuthSession(true);
      setData(session);
      setLoading(false);
    },
  };
}
